import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { Heading } from 'Components/_theme/Heading/Heading'
import { Text } from 'Components/_theme/Text/Text'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'

import { useStore } from 'Stores/index'

import { AVAILABLE_LANGUAGES, isSupportedLanguage } from 'Constants/locale'

import { checkPublicReturnLabelToken } from './PublicRequestReturnLabel.api'
import {
  type PublicRequestReturnLabelFormValues,
  PublicRequestReturnLabelStateEnum,
  PublicRequestReturnLabelTokenFlowStateEnum,
} from './PublicRequestReturnLabel.types'
import { buildSubmitPayload } from './PublicRequestReturnLabel.utils'
import { PublicRequestReturnLabelForm } from './PublicRequestReturnLabelForm'
import { PublicRequestReturnLabelHeader } from './PublicRequestReturnLabelHeader'
import { PublicRequestReturnLabelSuccess } from './PublicRequestReturnLabelSuccess'
import { usePublicReturnLabelSubmit } from './usePublicReturnLabelSubmit'

export const PublicRequestReturnLabel = () => {
  const { orgHash, token } = useParams<{ orgHash?: string; token?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { portalSettingsStore } = useStore()
  const isTokenRoute = token !== undefined
  const [pageState, setPageState] = useState<PublicRequestReturnLabelStateEnum>(
    PublicRequestReturnLabelStateEnum.FORM,
  )
  const [tokenFlowState, setTokenFlowState] =
    useState<PublicRequestReturnLabelTokenFlowStateEnum | null>(
      token === undefined
        ? null
        : PublicRequestReturnLabelTokenFlowStateEnum.CHECKING,
    )
  const [checkedToken, setCheckedToken] = useState<string | null>(token ?? null)
  const [successData, setSuccessData] = useState<{
    hasDangerousGoods: boolean
    requestToken: string
    trackingReference: string
  } | null>(null)
  const [orgCountry, setOrgCountry] = useState<string | null>(null)
  const [showCustomerSupportCard, setShowCustomerSupportCard] = useState(false)
  const [showLabelComingSoon, setShowLabelComingSoon] = useState(false)
  const { hasSubmitError, isSubmitting, submit } = usePublicReturnLabelSubmit()
  const requestToken = (isTokenRoute ? token : orgHash) ?? ''

  const isTokenCheckPending =
    isTokenRoute &&
    (tokenFlowState === PublicRequestReturnLabelTokenFlowStateEnum.CHECKING ||
      checkedToken !== token)
  const showManualDevices =
    !isTokenRoute ||
    tokenFlowState ===
      PublicRequestReturnLabelTokenFlowStateEnum.FALLBACK_MANUAL ||
    tokenFlowState === PublicRequestReturnLabelTokenFlowStateEnum.VALID_MANUAL

  useEffect(() => {
    const lang = searchParams.get('lang')

    if (!lang || !isSupportedLanguage(lang)) {
      return
    }

    if (portalSettingsStore.i18n.language === lang) {
      setSearchParams(
        prev => {
          prev.delete('lang')

          return prev
        },
        { replace: true },
      )

      return
    }

    portalSettingsStore.setI18n(AVAILABLE_LANGUAGES[lang])
  }, [
    searchParams,
    setSearchParams,
    portalSettingsStore,
    portalSettingsStore.i18n.language,
  ])

  useEffect(() => {
    if (!isTokenRoute) {
      return
    }

    if (!token) {
      setCheckedToken(null)
      setTokenFlowState(
        PublicRequestReturnLabelTokenFlowStateEnum.FALLBACK_MANUAL,
      )

      return
    }

    let isActive = true

    setCheckedToken(token)
    setTokenFlowState(PublicRequestReturnLabelTokenFlowStateEnum.CHECKING)

    checkPublicReturnLabelToken(token).then(result => {
      if (!isActive) {
        return
      }

      switch (result.kind) {
        case 'regenerated':
          if (result.newToken === token) {
            setTokenFlowState(
              PublicRequestReturnLabelTokenFlowStateEnum.FALLBACK_MANUAL,
            )

            return
          }

          navigate(
            GLOBAL_ROUTE_PATHS.public.requestReturnLabelToken.replace(
              ':token',
              result.newToken,
            ),
            { replace: true },
          )

          return
        case 'valid_prefilled':
          setTokenFlowState(
            PublicRequestReturnLabelTokenFlowStateEnum.VALID_PREFILLED,
          )
          setOrgCountry(result.orgCountry ?? null)

          return
        case 'valid_no_identifier':
          setTokenFlowState(
            PublicRequestReturnLabelTokenFlowStateEnum.FALLBACK_MANUAL,
          )
          setOrgCountry(result.orgCountry ?? null)

          return
        case 'invalid':
          setTokenFlowState(
            PublicRequestReturnLabelTokenFlowStateEnum.FALLBACK_MANUAL,
          )

          return
        default:
          result satisfies never
      }
    })

    return () => {
      isActive = false
    }
  }, [isTokenRoute, token, navigate])

  const handleSubmit = async (data: PublicRequestReturnLabelFormValues) => {
    const response = await submit(buildSubmitPayload(data, requestToken))

    if (!response) {
      return
    }

    if (response.showCustomerSupportCard) {
      setShowCustomerSupportCard(true)

      return
    }

    if (response.showLabelComingSoon) {
      setShowLabelComingSoon(true)

      return
    }

    setSuccessData({
      hasDangerousGoods: response.hasDangerousGoods,
      requestToken,
      trackingReference: response.trackingReference,
    })
    setPageState(PublicRequestReturnLabelStateEnum.SUCCESS)
  }

  const handleUsePrefilled = () => {
    setTokenFlowState(
      PublicRequestReturnLabelTokenFlowStateEnum.VALID_PREFILLED,
    )
  }

  const handleUseManual = () => {
    setTokenFlowState(PublicRequestReturnLabelTokenFlowStateEnum.VALID_MANUAL)
  }

  return (
    <div className="PublicRequestReturnLabel">
      <PublicRequestReturnLabelHeader />

      {pageState === PublicRequestReturnLabelStateEnum.SUCCESS &&
      successData ? (
        <div className="PublicRequestReturnLabel-wrapper">
          <PublicRequestReturnLabelSuccess
            hasDangerousGoods={successData.hasDangerousGoods}
            requestToken={successData.requestToken}
            trackingReference={successData.trackingReference}
          />
        </div>
      ) : (
        <div className="PublicRequestReturnLabel-wrapper">
          <div className="PublicRequestReturnLabel-container p-6 p-md-9">
            <div className="text-center mb-9">
              <Heading className="mb-3" size={1}>
                <FormattedMessage id="PublicRequestReturnLabel_title" />
              </Heading>
              <Text className="PublicRequestReturnLabel-description text-muted">
                <FormattedMessage id="PublicRequestReturnLabel_description" />
              </Text>
            </div>

            {isTokenCheckPending ? (
              <PageLoadingState />
            ) : (
              <PublicRequestReturnLabelForm
                hasSubmitError={hasSubmitError}
                isSubmitting={isSubmitting}
                onSubmit={handleSubmit}
                onUseManualDevices={handleUseManual}
                onUsePrefilledDevices={handleUsePrefilled}
                showCustomerSupportCard={showCustomerSupportCard}
                showDeviceSection={showManualDevices}
                showLabelComingSoon={showLabelComingSoon}
                showTokenModeBanner={
                  isTokenRoute &&
                  tokenFlowState ===
                    PublicRequestReturnLabelTokenFlowStateEnum.VALID_PREFILLED
                }
                showUsePrefilledButton={
                  tokenFlowState ===
                  PublicRequestReturnLabelTokenFlowStateEnum.VALID_MANUAL
                }
                topCountryCode={orgCountry}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
